'use client';

import { motion } from 'motion/react';
import Image from 'next/image';
import { useState } from 'react';

import { REVEAL_STAGGER, TextReveal } from '@/components/elements/text-reveal';

const STATS = [
  { value: '50K+', label: 'photos shared' },
  { value: '2,000+', label: 'events captured' },
  { value: '30K+', label: 'guests contributing' },
] as const;

export function Features() {
  return (
    <section
      id="features"
      className="section-padding relative scroll-mt-20 overflow-hidden"
    >
      {/* Dot grid pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 mask-t-from-30% mask-t-to-100% mask-b-from-30% mask-b-to-100% opacity-[0.15]"
        style={{
          backgroundImage:
            'radial-gradient(circle, currentColor 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="relative container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
        >
          <motion.div
            className="mb-12 text-center md:mb-20 md:text-left"
            variants={{
              hidden: {},
              visible: {},
            }}
          >
            <TextReveal mode="animate" delay={REVEAL_STAGGER}>
              <h2 className="text-4xl leading-none tracking-tighter md:text-5xl lg:text-6xl">
                Keeps is built for real moments
              </h2>
            </TextReveal>
            <TextReveal
              mode="animate"
              delay={REVEAL_STAGGER * 2}
              className="mt-4"
            >
              <p className="text-muted-foreground mx-auto max-w-lg text-lg leading-relaxed md:mx-0">
                Features designed to make capturing memories effortless
              </p>
            </TextReveal>
          </motion.div>
          <motion.div
            variants={{
              hidden: {},
              visible: { transition: { delayChildren: 0.4 } },
            }}
          >
            <StatsPanel />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function StatsPanel() {
  const [hoveredStat, setHoveredStat] = useState<number | null>(null);

  return (
    <motion.div
      className="dark:border-border dark:bg-surface relative flex flex-col gap-10 rounded-xl bg-[#121212] px-8 py-12 shadow-[0px_12px_14px_rgba(0,0,0,0.14)] md:h-80 md:flex-row md:items-center md:gap-0 md:px-16 dark:border"
      onMouseLeave={() => setHoveredStat(null)}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.1 } },
      }}
    >
      {STATS.map((stat, i) => (
        <div key={stat.label} className="contents">
          {i > 0 && (
            <div
              aria-hidden
              className="mx-8 hidden h-[140px] w-px shrink-0 bg-white/10 md:block lg:mx-10"
            />
          )}
          <motion.div
            className="cursor-default"
            onMouseEnter={() => setHoveredStat(i)}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
              },
            }}
            animate={{
              opacity: hoveredStat === null || hoveredStat === i ? 1 : 0.4,
            }}
            transition={{ duration: 0.3 }}
          >
            <motion.span
              className="font-display block text-6xl tracking-tight text-white lg:text-7xl"
              animate={{
                x: hoveredStat === i ? 4 : 0,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {stat.value}
            </motion.span>
            <span className="mt-3 block font-mono text-[11px] tracking-[2px] text-white/55 uppercase">
              {stat.label}
            </span>
          </motion.div>
        </div>
      ))}

      {/* Tilted photo card */}
      <motion.div
        aria-hidden
        className="ml-auto hidden rotate-[5deg] lg:block"
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
          },
        }}
      >
        <div className="relative drop-shadow-[0px_8px_9px_rgba(0,0,0,0.3)]">
          <Image
            src="/images/hero/cards/big-day.jpg"
            alt=""
            width={312}
            height={236}
            className="h-[118px] w-[156px] rounded-[19px] object-cover"
          />
          <span className="bg-secondary text-secondary-foreground absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 text-[11px] whitespace-nowrap">
            💍 The big day
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}


